import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  DataService,
  PamlabApiServiceService,
  AuthService,
  RouteService,
  SnackbarService,
} from '@/shared/services';
import { courseCatalog } from '../../../../../data';
import { environment } from '../../../../../environments/environment';
import { StudentCourseExplorerParamsResponse } from '../../../../../server/schemas';
import {
  StudentNameAndID,
  TakenCourseAndGrade,
} from '../../../../../server/types';
import {
  courseExistsValidator,
  studentExistsValidator,
} from '@/shared/directives/custom-validators';
import { HttpErrorResponse } from '@angular/common/http';
import { Course } from '@/core/interfaces';

interface CourseRecommender {
  recommender_type: string;
  college: string;
  semester: string;
  courses: string[];
}

@Component({
  selector: 'course-recommender-form',
  templateUrl: './course-recommender-form.component.html',
  styleUrls: ['./course-recommender-form.component.css'],
  standalone: false,
})
export class CourseRecommenderFormComponent implements OnInit, OnDestroy {
  public colors = environment['colors'];
  public courseSelectHeight!: string;
  public studentSelectHeight!: string;

  public emptyInput: boolean = true;

  // variable to check if form is fully filled in
  isCoursesTakenEmpty: boolean = true;

  // subscription for data
  private subscription: Subscription = new Subscription();

  // observable string that observes for input value in the form field
  filteredCourses!: string[];

  // array of strings => courses picked by user
  public courses: string[] = [];

  // course catalog variable with code and name
  courseCatalogWithName: string[] = [];

  // mat-chip
  selectable = true;
  removable = true;

  // variable to check if user form submission is same as previous submission
  prevParam: CourseRecommender = {
    recommender_type: '',
    college: '',
    semester: '',
    courses: [],
  };
  public studentAdditionalInfo: StudentCourseExplorerParamsResponse = {
    cohort: 0,
    program: '',
    semesters: [],
    taken_courses: [],
    college: { text: '', value: '' },
  };
  public targetCourseOptionsBoxHeight!: string;
  public courseToAddOptionsBoxHeight!: string;
  public takenCoursesAndGrades: string[] = [];
  public initTakenCoursesAndGrades: string[] = [];
  public isCoursesEmpty: boolean = true;
  // public selectedSemester: string = '';
  public showTakenCoursesReset: boolean = false;
  public courseTakenData: TakenCourseAndGrade[] = [];
  public studenNamesAndIDs!: StudentNameAndID[];
  public studenNamesAndIDsFiltered!: StudentNameAndID[];
  private studentPattern = /^[A-Za-z]+(\s[A-Za-z]+)+\s\(970\d{6}\)$/;
  private removedCourses: { [course_code: string]: string } = {};

  // variable to track user role
  public userRole = this._authService.getRole();

  /////////////////////////////////////////////////// FORM GROUP DECLARATION ///////////////////////////////////////////////////
  // courses form group
  public coursesForm = new FormGroup({
    course: new FormControl<string>(''),
    course_grade: new FormControl(),
  });

  // recommender courses form group
  public recommenderCoursesForm = new FormGroup({
    recommender_type: new FormControl('', [Validators.required]),
    semester: new FormControl('', [Validators.required]),
    student: new FormControl('', [
      Validators.required,
      Validators.pattern(this.studentPattern),
    ]),
    cohort: new FormControl({ value: '', disabled: true }),
    college: new FormControl({ value: '', disabled: true }),
    program: new FormControl({ value: '', disabled: true }),
  });

  constructor(
    private _dialog: MatDialog,
    private _apiService: PamlabApiServiceService,
    private _dataService: DataService,
    private snackbarService: SnackbarService,
    private _routeService: RouteService,
    private _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.courseCatalogWithName = courseCatalog.map(
      (course: Course) => `${course.course_code}-${course.course_name}`,
    );
    this.updateCoursesValidator();

    // if a student logged in get courses taken
    if (this.userRole === 'student') {
      const studentIdField = this.recommenderCoursesForm.get('student')!;
      studentIdField.setValue(this._authService.getStudentNameAndId());
      studentIdField.disable();
      this.getStudentCohortAndProgram(this._authService.getStudentID());
    } else {
      this._apiService.fetchStudentNamesAndIDs().subscribe((data) => {
        this.studenNamesAndIDs = data;
        this.studenNamesAndIDsFiltered = [...data];
        this.recommenderCoursesForm
          .get('student')!
          .setValidators(studentExistsValidator(data));

        this.subscription.add(
          this.recommenderCoursesForm
            .get('student')!
            .valueChanges.pipe(
              startWith(''),
              map((value) => {
                this.studenNamesAndIDsFiltered = this.studenNamesAndIDs.filter(
                  (student) =>
                    `${student.name}${student.uwf_id.toString()}`
                      .toLowerCase()
                      .includes(value!.toLowerCase()),
                );

                if (this.studenNamesAndIDsFiltered.length < 4) {
                  this.studentSelectHeight =
                    this.studenNamesAndIDsFiltered.length * 50 + 'px';
                } else {
                  this.studentSelectHeight = '200px';
                }
              }),
            )
            .subscribe(),
        );
      });
    }

    // observable to observes for changes in the input filed for course name
    // For Course Recommender
    this.subscription.add(
      this.coursesForm
        .get('course')!
        .valueChanges.pipe(
          startWith(''),
          map((value) => {
            if (value !== null) {
              this.filteredCourses = this.courseCatalogWithName.filter(
                (course) =>
                  course.toLowerCase().startsWith(value!.toLowerCase()),
              );

              if (this.filteredCourses.length < 4) {
                this.courseSelectHeight =
                  this.filteredCourses.length * 50 + 'px';
              } else {
                this.courseSelectHeight = '200px';
              }
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    setTimeout(() => {
      this.subscription.unsubscribe();
    }, 3000);
  }

  recommenderCoursesFormSubmit() {
    this._dataService.setPendingData(true);
    const params = {
      recommender_type: this.recommenderCoursesForm.value.recommender_type!,
      college: this.studentAdditionalInfo.college!.value,
      semester: this.recommenderCoursesForm.value.semester!,
      courses_taken: this.takenCoursesAndGrades,
    };

    this.subscription.add(
      this._apiService.getRecommededCourses(params).subscribe({
        next: (data) => {
          this._dataService.setData({ data });
          this._dataService.setPendingData(false);
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          }
          this._dataService.setData({ error: errorRes.error });
          this._dataService.setPendingData(false);
        },
      }),
    );
  }

  // split string into 2 parts [MAC2311, Calculus 1]
  getCourseCode(course: string, delimiter = '-') {
    return course.split(delimiter)[0];
  }

  // add course to courses array
  addCourses() {
    const courseName = this.coursesForm.value.course!;
    const crsCode = this.getCourseCode(courseName);
    this.removeCourseFromList(courseName);
    this.updateCoursesValidator();

    // join strings together for submission in backend
    // Example ==> MAC1105:A
    let str = crsCode + ':' + this.coursesForm.value.course_grade;
    !this.takenCoursesAndGrades.includes(str) &&
      this.takenCoursesAndGrades.push(str);

    // reset form values
    this._resetCourseAdditionForm();

    this.showTakenCoursesReset = !this._arraysHaveSameElements(
      this.takenCoursesAndGrades,
      this.initTakenCoursesAndGrades,
    );
  }

  _resetCourseAdditionForm() {
    this.coursesForm.setValue({ course: '', course_grade: '' });
    this.coursesForm.reset();
  }

  _arraysHaveSameElements(arr1: string[], arr2: string[]) {
    return (
      arr1.length === arr2.length &&
      arr1.every((element) => arr2.includes(element))
    );
  }

  // remove course in courses[]
  removeCourse(crsWithGrade: string): void {
    const index = this.takenCoursesAndGrades.indexOf(crsWithGrade);

    const crsCode = this.getCourseCode(crsWithGrade, ':');
    this.addCourseTolist(crsCode);

    if (index >= 0) {
      this.takenCoursesAndGrades.splice(index, 1);
    }

    if (this.takenCoursesAndGrades.length === 0) {
      this.isCoursesEmpty = true;
    }
    this.showTakenCoursesReset = !this._arraysHaveSameElements(
      this.takenCoursesAndGrades,
      this.initTakenCoursesAndGrades,
    );
  }

  // check course grade dropdown field to see if it's empty
  //if empty add course button will be disabled
  checkDropdown(input: any) {
    this.emptyInput = input.value === '';
  }

  // close popup _dialog after form is submitted
  closeDialog() {
    this._dialog.closeAll();
  }

  // function to check if isCoursesTakenEmpty and if the form is fully filled
  // if it is submit button will be enabled for submission
  disabledSubmitButton() {
    if (this.recommenderCoursesForm.valid && !this.isCoursesEmpty) {
      return false;
    } else {
      return true;
    }
  }

  resetForm() {
    const studentIDControl = this.recommenderCoursesForm.get(
      'student',
    ) as FormControl;
    studentIDControl.reset();

    const cohortControl = this.recommenderCoursesForm.get(
      'cohort',
    ) as FormControl;
    cohortControl.reset();

    const programControl = this.recommenderCoursesForm.get(
      'program',
    ) as FormControl;
    programControl.reset();

    this.takenCoursesAndGrades = [];
  }

  // getting all of student info
  getStudentCohortAndProgram(studentId: number) {
    this.takenCoursesAndGrades = [];
    this.initTakenCoursesAndGrades = [];

    this.subscription.add(
      this._apiService.getCohortProgramOfStudent(studentId).subscribe({
        next: (data) => {
          this.studentAdditionalInfo = data;
          this.takenCoursesAndGrades.push(
            ...new Set(
              this.studentAdditionalInfo.taken_courses.map(
                (crs) => `${crs.course_code}:${crs.course_grade}`,
              ),
            ),
          );

          this.removeTakenCoursesFromCourseList();

          this.initTakenCoursesAndGrades = [...this.takenCoursesAndGrades];

          this.recommenderCoursesForm
            .get('cohort')
            ?.setValue(this.studentAdditionalInfo.cohort.toString());

          this.recommenderCoursesForm
            .get('program')
            ?.setValue(this.studentAdditionalInfo.program);

          this.recommenderCoursesForm
            .get('college')
            ?.setValue(this.studentAdditionalInfo.college!.text);

          // Set default
          this.recommenderCoursesForm
            .get('semester')
            ?.setValue(this.studentAdditionalInfo.semesters[0].text);

          this.isCoursesEmpty = this.takenCoursesAndGrades.length === 0;
          // this.selectedSemester = 'Prior to Spring 1';
          this.showTakenCoursesReset = false;
        },
        error: (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 401) {
            this._routeService.navigate(environment['login_url']);
          } else {
            this.snackbarService.open(errorRes.error.message, 'error');
          }
        },
      }),
    );
  }

  clearStudentInfo() {
    this.recommenderCoursesForm.patchValue({
      student: '',
      cohort: '',
      college: '',
      semester: '',
      program: '',
    });
    this.studentAdditionalInfo.semesters = [];
    this.takenCoursesAndGrades = [];
    this.initTakenCoursesAndGrades = [];
  }

  viewStudent() {
    const queryParams = {
      student: this.recommenderCoursesForm.get('student')!.value!,
    };
    this._routeService.navigateNewTab('/student-profile', queryParams);
  }

  // organize course taken data to display in the summer/fall history courses box
  organizeCoursesTakenData(
    coursesTakenArr: { course_code: string; course_grade: string }[],
  ) {
    coursesTakenArr.forEach(
      (element: { course_code: string; course_grade: string }) => {
        this.takenCoursesAndGrades.push(
          `${element.course_code}:${element.course_grade}`,
        );
      },
    );
    this.initTakenCoursesAndGrades = [...this.takenCoursesAndGrades];

    this.isCoursesEmpty = false;
  }

  invalidCourseToAddForm() {
    const courseCode = this.coursesForm.get('course');
    const courseGrade = this.coursesForm.get('course_grade');
    return !courseCode?.value || !courseGrade?.value;
  }

  // function to check if isCoursesEmpty and if the form is fully filled
  // if it is submit button will be enabled for submission
  disabledFormSubmitButton() {
    return (
      !this.recommenderCoursesForm.valid ||
      !this.takenCoursesAndGrades.length ||
      !this.recommenderCoursesForm.get('recommender_type')?.value ||
      !this.recommenderCoursesForm.get('semester')?.value
    );
  }

  resetTakenCourses() {
    this.takenCoursesAndGrades = [...this.initTakenCoursesAndGrades];
    this.showTakenCoursesReset = false;

    const removedCourses = Object.values(this.removedCourses);
    this.courseCatalogWithName.push(...removedCourses);
    this.courseCatalogWithName = [...new Set(this.courseCatalogWithName)];
    this.filteredCourses.push(...removedCourses);
    this.filteredCourses = [...new Set(this.filteredCourses)];
    this.removeTakenCoursesFromCourseList();
    this.updateCoursesValidator();
  }

  private removeCourseFromList(crs: string) {
    this.courseCatalogWithName = this.courseCatalogWithName.filter(
      (x) => x !== crs,
    );
    this.filteredCourses = this.filteredCourses.filter((x) => x !== crs);
    this.removedCourses[this.getCourseCode(crs)] = crs;
  }

  private addCourseTolist(crsCode: string) {
    const fullCrsName = this.removedCourses[crsCode];
    this.courseCatalogWithName.push(fullCrsName);
    this.filteredCourses.push(fullCrsName);
    this.courseCatalogWithName = [...new Set(this.courseCatalogWithName)];
    this.filteredCourses = [...new Set(this.filteredCourses)];
    this.updateCoursesValidator();
  }

  private removeTakenCoursesFromCourseList() {
    this.studentAdditionalInfo.taken_courses.forEach((crs) => {
      const courseInfo = courseCatalog.find(
        (x) => x.course_code === crs.course_code,
      )!;
      if (courseInfo) {
        const fullCrsName = `${courseInfo.course_code}-${courseInfo.course_name}`;
        this.removeCourseFromList(fullCrsName);
      }
    });
    this.updateCoursesValidator();
  }

  private updateCoursesValidator() {
    this.coursesForm
      .get('course')!
      .setValidators([courseExistsValidator(this.courseCatalogWithName)]);

    this.coursesForm.updateValueAndValidity();
  }
}
